import { useState, useEffect, useRef } from "react";
import "./App.css";
import AddressGenerator from "./components/AddressGenerator";
import MessageSigner from "./components/MessageSigner";
import FunctionalSigner from "./components/FunctionalSigner";
import AgentDelegation from "./components/AgentDelegation";
import StatusPanel from "./components/StatusPanel";
import SDKTestPage from "./test_pages/SDKTestPage";
import WebSocketTestPage from "./test_pages/WebSocketTestPage";
import LayoutTestPage from "./test_pages/LayoutTestPage";
import HooksTestPage from "./test_pages/HooksTestPage";
import TradingTestPage from "./test_pages/TradingTestPage";
import SwapTestUI from "./test_pages/swap_test_ui";
import ExplorerTestPage from "./test_pages/ExplorerTestPage";
import { useMormSigner } from "./hooks";
import { AppProvider } from "./context/AppContext";
import { Toaster } from "./components/ui/Toaster";

type Page =
  | "main"
  | "sdk-test"
  | "websocket-test"
  | "layout-test"
  | "hooks-test"
  | "trading-test"
  | "swap-test"
  | "explorer-test";

// Map URL paths to page types
const pathToPage: Record<string, Page> = {
  "/": "main",
  "/main": "main",
  "/sdk-test": "sdk-test",
  "/websocket-test": "websocket-test",
  "/layout-test": "layout-test",
  "/hooks-test": "hooks-test",
  "/trading-test": "trading-test",
  "/swap-test": "swap-test",
  "/explorer-test": "explorer-test",
};

// Map page types to URL paths
const pageToPath: Record<Page, string> = {
  main: "/",
  "sdk-test": "/sdk-test",
  "websocket-test": "/websocket-test",
  "layout-test": "/layout-test",
  "hooks-test": "/hooks-test",
  "trading-test": "/trading-test",
  "swap-test": "/swap-test",
  "explorer-test": "/explorer-test",
};

/**
 * Get current page from URL pathname
 */
function getPageFromPath(): Page {
  const path = window.location.pathname;
  return pathToPage[path] || "main";
}

// Available video backgrounds
const VIDEO_OPTIONS = [
  { id: "Id8oZS95KxQ", name: "Summer Wave" },
  { id: "y9PTNTSpGJs", name: "Wave Ocean" },
  { id: "KkX9LfqeOF4", name: "Morph Lava" },
  { id: "bx-0YlFprqc", name: "DAG Rev." },
  { id: "eCHaiEjCDFM", name: "Black Swarm 1" },
  { id: "Tbw3w0RA0WU", name: "Lofi Leon Skyline" },
  { id: "tjetD2pFK4A", name: "Lofi Sinth Skyline" },
  { id: "aG01KIXWrbU", name: "Lofi Doom Skyline" },

  { id: "ZWyv_w_cFiw", name: "Deep Cybercity Japan" },
  { id: "wVqeChtoY_k", name: "Synthwave" },
  { id: "0KgxkD1u-TI", name: "City Travel" },

  { id: "wyxxPTFfdi8", name: "Pearl Liquid" },
  { id: "ytXX67Ghies", name: "Smoke Liquid" },
  { id: "aYEg66Egjo0", name: "Home Hollywood" },
  { id: "AfSWldaT-AE", name: "Mega Hollywood" },
  { id: "0JQkT1FxhEc", name: "GT7 Audi RS 5 Turbo DTM" },
  { id: "VZyzqGgEhZc", name: "Forza Fujimi Kaido" },
  { id: "UYJIqBZENjU", name: "RC プラネットコースター水中" },
  { id: "BQL-CP8d1-o", name: "RC Iron Menace" },
  { id: "oAJLKDMihnU", name: "RC Knoebels Amusement Park" },
  { id: "JggE0FYV_Ys", name: "RC キングスアイランド サン・オブ・ビースト" },
  { id: "2GVmvtVA5p8", name: "RC Unlimited" },
  { id: "q-70DtTlkKU", name: "RC King Kong River" },
  { id: "4Uq9Hk3E7hs", name: "Ballance Twilight Zone" },
  { id: "WCOKSsygnxM", name: "Ballance 2004 Zone" },
  { id: "ZP4d40DV7K0", name: "Ballance 210522 Zone" },
  { id: "NfxnqErfkL8", name: "Dying Light Zone" },
];

function App(): JSX.Element {
  const { wasmStatus, isReady } = useMormSigner();
  const [currentPage, setCurrentPage] = useState<Page>(getPageFromPath());
  const [videoBackgroundEnabled, setVideoBackgroundEnabled] = useState(false);
  const [videoUrl, setVideoUrl] = useState("Id8oZS95KxQ"); // Default YouTube video ID
  const audioRef = useRef<HTMLAudioElement>(null);
  const audioStreamUrl = "/s0203.mp3"; // Audio file from public folder

  // Sync URL with initial page state on mount
  useEffect(() => {
    const currentPath = window.location.pathname;
    const expectedPath = pageToPath[currentPage];
    if (currentPath !== expectedPath) {
      window.history.replaceState({ page: currentPage }, "", expectedPath);
    }
  }, []); // Only run on mount

  // Sync page state with URL changes (browser back/forward buttons)
  useEffect(() => {
    const handlePopState = () => {
      setCurrentPage(getPageFromPath());
    };
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  // Update URL when page changes
  const navigateToPage = (page: Page) => {
    setCurrentPage(page);
    const path = pageToPath[page];
    window.history.pushState({ page }, "", path);
  };

  // Listen for custom event to navigate to websocket test page
  useEffect(() => {
    const handleNavigate = () => {
      navigateToPage("websocket-test");
    };
    window.addEventListener("navigateToWebSocketTest", handleNavigate);
    return () => {
      window.removeEventListener("navigateToWebSocketTest", handleNavigate);
    };
  }, []);

  // Extract YouTube video ID from URL
  const getYouTubeVideoId = (url: string): string => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : url;
  };

  const toggleVideoBackground = () => {
    setVideoBackgroundEnabled(!videoBackgroundEnabled);
  };

  // Control background music based on video background state
  useEffect(() => {
    if (audioRef.current) {
      if (videoBackgroundEnabled) {
        audioRef.current.play().catch((error: unknown) => {
          console.error("Error playing background music:", error);
        });
      } else {
        audioRef.current.pause();
      }
    }
  }, [videoBackgroundEnabled]);

  return (
    <AppProvider>
      <div
        className={`App ${videoBackgroundEnabled ? "video-background-enabled" : ""}`}
      >
        {/* Background Music */}
        <audio
          ref={audioRef}
          src={audioStreamUrl}
          loop
          preload="auto"
          crossOrigin="anonymous"
        />

        {/* YouTube Video Background */}
        {videoBackgroundEnabled && (
          <div className="video-background-container">
            <iframe
              key={videoUrl}
              className="video-background"
              src={`https://www.youtube.com/embed/${getYouTubeVideoId(videoUrl)}?autoplay=1&mute=1&loop=1&playlist=${getYouTubeVideoId(videoUrl)}&controls=0&showinfo=0&rel=0&iv_load_policy=3&modestbranding=1&playsinline=1`}
              frameBorder="0"
              allow="autoplay; encrypted-media"
              allowFullScreen
              title="Background Video"
            />
            <div className="video-background-overlay"></div>
          </div>
        )}

        {/* Video Background Controls */}
        <div className="video-background-controls">
          <select
            className="video-background-select"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            disabled={!videoBackgroundEnabled}
            title="Select Video Background"
          >
            {VIDEO_OPTIONS.map((video) => (
              <option key={video.id} value={video.id}>
                {video.name}
              </option>
            ))}
          </select>
          <button
            className="video-background-toggle"
            onClick={toggleVideoBackground}
            title={
              videoBackgroundEnabled
                ? "Disable Video Background"
                : "Enable Video Background"
            }
          >
            {videoBackgroundEnabled ? "🎬" : "📹"}
          </button>
        </div>

        <header className="App-header">
          <h3>SDK, Testing, QA</h3>
          {/* Navigation */}
          <nav>
            <button
              onClick={() => navigateToPage("main")}
              className={`nav-button ${currentPage === "main" ? "active" : ""}`}
            >
              Morm Signer
            </button>
            <button
              onClick={() => navigateToPage("sdk-test")}
              className={`nav-button ${currentPage === "sdk-test" ? "active" : ""}`}
            >
              SDK Trade API
            </button>
            <button
              onClick={() => navigateToPage("websocket-test")}
              className={`nav-button ${currentPage === "websocket-test" ? "active" : ""}`}
            >
              WebSocket Test
            </button>
            <button
              onClick={() => navigateToPage("hooks-test")}
              className={`nav-button ${currentPage === "hooks-test" ? "active" : ""}`}
            >
              Hooks Test
            </button>
            <button
              onClick={() => navigateToPage("layout-test")}
              className={`nav-button ${currentPage === "layout-test" ? "active" : ""}`}
            >
              Layout Test
            </button>
            <button
              onClick={() => navigateToPage("trading-test")}
              className={`nav-button ${currentPage === "trading-test" ? "active" : ""}`}
            >
              Trading Test
            </button>
            <button
              onClick={() => navigateToPage("swap-test")}
              className={`nav-button ${currentPage === "swap-test" ? "active" : ""}`}
            >
              Swap Test
            </button>
            <button
              onClick={() => navigateToPage("explorer-test")}
              className={`nav-button ${currentPage === "explorer-test" ? "active" : ""}`}
            >
              Explorer Test
            </button>
          </nav>
        </header>

        <main className="App-main">
          {currentPage === "sdk-test" ? (
            <SDKTestPage />
          ) : currentPage === "websocket-test" ? (
            <WebSocketTestPage />
          ) : currentPage === "layout-test" ? (
            <LayoutTestPage />
          ) : currentPage === "hooks-test" ? (
            <HooksTestPage />
          ) : currentPage === "trading-test" ? (
            <TradingTestPage />
          ) : currentPage === "swap-test" ? (
            <SwapTestUI />
          ) : currentPage === "explorer-test" ? (
            <ExplorerTestPage />
          ) : (
            <>
              <h1>Morm SDK</h1>
              <p>
                Test WebAssembly integration for morm_signer with Agent
                Delegation
              </p>
              <StatusPanel status={wasmStatus} />

              {isReady && (
                <div className="App-content gridarrangement">
                  <div className="App-section">
                    <AddressGenerator />
                  </div>

                  <div className="App-section">
                    <MessageSigner />
                  </div>

                  <div className="App-section">
                    <FunctionalSigner />
                  </div>

                  <div className="App-section">
                    <AgentDelegation />
                  </div>
                </div>
              )}

              {wasmStatus.error && (
                <div className="App-error">
                  <h3>Error Loading WASM</h3>
                  <p>{wasmStatus.error}</p>
                  <p>
                    Make sure the morm_signer.wasm file is available in the
                    public directory.
                  </p>
                </div>
              )}
            </>
          )}
        </main>
      </div>
      <Toaster
        position="bottom-right"
        toastOptions={{
          duration: 4000,
          style: {
            color: "var(--foreground)",
            border: "1px solid var(--border)",
          },
        }}
      />
    </AppProvider>
  );
}

export default App;
